/**
 * line-receipts.rules.ts
 *
 * Pure helpers for a PO line's individual receipt rows (`lineItems[].receipts`),
 * which sit behind the aggregate `qtyReceived`. A line is partial in one of two
 * ways:
 *
 *  - Quantity partial — a countable line received in part (`qtyReceived <
 *    qtyExpected`); its receipts carry numeric `quantityReceived`.
 *  - Ragged / qualitative partial — a multi-part line (kit/bundle) where
 *    individual PARTS arrive rather than a count. Its receipts carry
 *    `quantityReceived: null` with the part in `itemDescription`; the aggregate
 *    `qtyReceived` stays 0, so these receipts are the only place its progress
 *    shows, and the line closes only when a receipt has `completesLine: true`.
 */

import { ReceivingLogReceipt } from '../../../models/poData/ReceivingStatus';

/**
 * Whether a line is ragged — its tracked receipts include part rows
 * (`quantityReceived: null`) rather than pure counts. Ragged lines are shown by
 * their part descriptions instead of an X/Y count.
 *
 * @param receipts - The line's prior receipt rows (may be absent on mock rows).
 * @returns True when at least one receipt is a part row.
 */
export function isRaggedLine(receipts: ReceivingLogReceipt[] | undefined | null): boolean {
  return (receipts ?? []).some((r) => r.quantityReceived == null);
}

/**
 * Splits a free-text ragged-parts entry ("part A (bracket), part B") into
 * individual part names, so each becomes its own receipt at submission
 * (`quantityReceived: null`, the part in `itemDescription`). Splits on commas
 * and newlines, trims, and drops blanks.
 *
 * @param partsText - The user's free-text parts entry for this delivery.
 * @returns One trimmed part name per entry; `[]` when the text is blank.
 */
export function splitRaggedParts(partsText: string | undefined | null): string[] {
  return (partsText ?? '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}
